import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Tooltip,
  Fade,
  Portal,
} from '@mui/material';
import {
  Help as HelpIcon,
  Lightbulb as LightbulbIcon,
  PlayCircle as PlayCircleIcon,
  Info as InfoIcon,
  School as SchoolIcon,
} from '@mui/icons-material';

const ICON_MAP = {
  help: HelpIcon,
  lightbulb: LightbulbIcon,
  play: PlayCircleIcon,
  info: InfoIcon,
  school: SchoolIcon,
};

const POSITION_OFFSETS = {
  top: { transform: 'translate(-50%, -100%)', marginTop: -8 },
  bottom: { transform: 'translate(-50%, 0)', marginTop: 8 },
  left: { transform: 'translate(-100%, -50%)', marginLeft: -8 },
  right: { transform: 'translate(0, -50%)', marginLeft: 8 },
  'top-left': { transform: 'translate(-100%, -100%)', marginTop: -8, marginLeft: -8 },
  'top-right': { transform: 'translate(0, -100%)', marginTop: -8, marginLeft: 8 },
  'bottom-left': { transform: 'translate(-100%, 0)', marginTop: 8, marginLeft: -8 },
  'bottom-right': { transform: 'translate(0, 0)', marginTop: 8, marginLeft: 8 },
};

const OnboardingLauncher = ({
  launcher,
  onActivate,
  onStartTour,
  theme,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const observerRef = useRef(null);

  const themeStyles = {
    primaryColor: theme?.primary_color || '#667eea',
    secondaryColor: theme?.secondary_color || '#764ba2',
    backgroundColor: theme?.background_color || '#ffffff',
    borderRadius: theme?.border_radius || 8,
  };

  const IconComponent = ICON_MAP[launcher?.icon] || HelpIcon;
  const positionOffset = POSITION_OFFSETS[launcher?.position] || POSITION_OFFSETS.right;

  const updatePosition = useCallback(() => {
    if (!launcher?.target_selector) return;

    const targetElement = document.querySelector(launcher.target_selector);
    
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      
      let top, left;
      
      switch (launcher.position) {
        case 'top':
        case 'top-left':
        case 'top-right':
          top = rect.top;
          break;
        case 'bottom':
        case 'bottom-left':
        case 'bottom-right':
          top = rect.bottom;
          break;
        default:
          top = rect.top + rect.height / 2;
      }

      switch (launcher.position) {
        case 'left':
        case 'top-left':
        case 'bottom-left':
          left = rect.left;
          break;
        case 'right':
        case 'top-right':
        case 'bottom-right':
          left = rect.right;
          break;
        default:
          left = rect.left + rect.width / 2;
      }

      setPosition({ top, left });
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [launcher?.target_selector, launcher?.position]);

  useEffect(() => {
    if (!launcher?.is_active) return;

    updatePosition();

    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    observerRef.current = new MutationObserver(() => {
      updatePosition();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [launcher?.is_active, updatePosition]);

  const handleClick = useCallback(() => {
    if (onActivate) {
      onActivate(launcher.id);
    }

    switch (launcher.action_type) {
      case 'tour':
        if (launcher.action_target && onStartTour) {
          onStartTour(launcher.action_target);
        }
        break;
      case 'url':
        if (launcher.action_target) {
          window.open(launcher.action_target, '_blank');
        }
        break;
      case 'custom':
        break;
      default:
        break;
    }
  }, [launcher, onActivate, onStartTour]);

  if (!launcher?.is_active || !isVisible) return null;

  return (
    <Portal>
      <Fade in={isVisible}>
        <Box
          sx={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 1100,
            ...positionOffset,
          }}
        >
          <Tooltip
            title={launcher.tooltip_text || launcher.name}
            placement="top"
            arrow
          >
            <IconButton
              onClick={handleClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: isHovered ? themeStyles.secondaryColor : themeStyles.primaryColor,
                color: 'white',
                boxShadow: 3,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: themeStyles.secondaryColor,
                  transform: 'scale(1.1)',
                },
                animation: isHovered ? 'none' : 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: `0 0 0 0 ${themeStyles.primaryColor}80`,
                  },
                  '70%': {
                    boxShadow: `0 0 0 10px ${themeStyles.primaryColor}00`,
                  },
                  '100%': {
                    boxShadow: `0 0 0 0 ${themeStyles.primaryColor}00`,
                  },
                },
              }}
            >
              <IconComponent sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>
    </Portal>
  );
};

OnboardingLauncher.propTypes = {
  launcher: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    target_selector: PropTypes.string.isRequired,
    icon: PropTypes.oneOf(['help', 'lightbulb', 'play', 'info', 'school']),
    tooltip_text: PropTypes.string,
    action_type: PropTypes.oneOf(['tour', 'checklist', 'url', 'custom']),
    action_target: PropTypes.string,
    position: PropTypes.oneOf([
      'top', 'bottom', 'left', 'right',
      'top-left', 'top-right', 'bottom-left', 'bottom-right',
    ]),
    is_active: PropTypes.bool,
  }),
  onActivate: PropTypes.func,
  onStartTour: PropTypes.func,
  theme: PropTypes.shape({
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string,
    background_color: PropTypes.string,
    border_radius: PropTypes.number,
  }),
};

export default React.memo(OnboardingLauncher);
